import { analytics, isTrackingEnabled, ANALYTICS_ANONYMOUS_ID_KEY, ANALYTICS_SESSION_KEY } from '../analytics/analytics';
import { createFileLogger } from '../../utils/logger';
import { getUserDemographics, getUserDemographicsForProfile } from '../../utils/userDemographics';
import { isLocalhost } from '../../utils/locationUtils';


interface UserAnalyticsProfile {
  anonymousId: string;
  userId?: string;
  sessionId: string;
  templates: {
    [templateId: string]: {
      accessCount: number;
      runCount: number;
      avgRefinements: number;
      avgCompletionTime: number;
      lastUsed: string;
    }
  };
  preferences: {
    favoriteCategories: string[];
    complexityPreference?: string;
    avgSessionLength: number;
  };
  createdAt: string;
  lastActiveAt: string;
}

class UserTrackingService {
  private anonymousId: string | null = null;
  private userId: string | null = null;
  private sessionId: string | null = null;
  private isInitialized = false;
  private log = createFileLogger('USER_TRACKING', { enabled: false });

  init(): void {
    if (this.isInitialized) {
      this.log.debug('User tracking already initialized');
      return;
    }

    if (!isTrackingEnabled()) {
      this.log.debug('[LOCALHOST] User tracking disabled for localhost');
      this.isInitialized = true;
      return;
    }

    // Use the analytics facade's storage keys so all systems agree on identity/session.
    this.anonymousId = localStorage.getItem(ANALYTICS_ANONYMOUS_ID_KEY) || this.generateUUID();
    localStorage.setItem(ANALYTICS_ANONYMOUS_ID_KEY, this.anonymousId);

    this.sessionId = sessionStorage.getItem(ANALYTICS_SESSION_KEY) || this.generateUUID();

    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      this.userId = localStorage.getItem('userId') || null;
    }

    const identityId = this.userId || this.anonymousId;

    const demographics = getUserDemographicsForProfile();
    const fullDemographics = getUserDemographics();

    const userProperties = {
      $name: this.userId || 'Anonymous User',
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      user_type: this.userId ? 'authenticated' : 'anonymous',
      initialized_at: new Date().toISOString(),
      device_type: demographics.device_type,
      screen_resolution: demographics.screen_resolution,
      browser_name: demographics.browser_name,
      browser_version: fullDemographics.browser_version,
      os_name: demographics.os_name,
      os_version: fullDemographics.os_version,
      timezone: demographics.timezone,
      timezone_offset: fullDemographics.timezone_offset,
      language: demographics.language,
      languages: fullDemographics.languages,
      currency: demographics.currency,
      color_scheme: demographics.color_scheme,
      reduced_motion: demographics.reduced_motion,
      touch_support: demographics.touch_support,
      cookies_enabled: fullDemographics.cookies_enabled,
      local_storage_enabled: fullDemographics.local_storage_enabled,
      online: fullDemographics.online,
      pixel_ratio: fullDemographics.pixel_ratio,
      connection_type: fullDemographics.connection_type,
      connection_downlink: fullDemographics.connection_downlink,
      memory: fullDemographics.memory,
      referrer: fullDemographics.referrer,
      entry_point: fullDemographics.entry_point,
      is_new_session: fullDemographics.is_new_session,
      viewport_width: fullDemographics.viewport_width,
      viewport_height: fullDemographics.viewport_height,
    };

    analytics.identify(identityId, userProperties);

    this.isInitialized = true;
    this.log.debug('User tracking initialized', {
      anonymousId: this.anonymousId,
      userId: this.userId ? '[REDACTED]' : null,
      sessionId: this.sessionId,
    });
  }

  setUser(userId: string): void {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return;
    }

    if (isLocalhost()) {
      this.log.debug('[LOCALHOST] User tracking disabled, skipping setUser');
      return;
    }

    const wasAnonymous = !this.userId;
    this.userId = userId;
    localStorage.setItem('userId', userId);

    const demographics = getUserDemographicsForProfile();

    analytics.identify(userId, {
      $name: userId,
      anonymous_id: this.anonymousId ?? undefined,
      user_type: 'authenticated',
      signup_date: wasAnonymous ? new Date().toISOString() : undefined,
      conversion_session: wasAnonymous ? this.sessionId : undefined,
      authenticated_at: new Date().toISOString(),
      ...demographics,
    });

    this.log.debug('User set as authenticated', { userId, wasAnonymous });
  }

  getCurrentUserId(): string {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return 'unknown';
    }
    return this.userId || this.anonymousId!;
  }

  getSessionId(): string {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return 'unknown';
    }
    return this.sessionId!;
  }

  getUserType(): 'authenticated' | 'anonymous' {
    return this.userId ? 'authenticated' : 'anonymous';
  }

  updateUserInfo(userInfo: {
    name?: string;
    email?: string;
    userId?: string;
    plan?: string;
    personalPlan?: string;
    isPaidUser?: boolean;
    ownsPaidOrg?: boolean;
    paidOrgsCount?: number;
  }): void {
    if (!this.isInitialized) {
      this.log.warn('User tracking not initialized. Call init() first.');
      return;
    }

    if (userInfo.userId && userInfo.userId !== this.userId) {
      this.setUser(userInfo.userId);
      return;
    }

    const properties: Record<string, any> = {};

    if (userInfo.name) properties.$name = userInfo.name;
    if (userInfo.email) properties.$email = userInfo.email;
    if (userInfo.plan) properties.plan = userInfo.plan;
    if (userInfo.personalPlan) properties.personal_plan = userInfo.personalPlan;
    if (userInfo.isPaidUser !== undefined) properties.is_paid_user = userInfo.isPaidUser;
    if (userInfo.ownsPaidOrg !== undefined) properties.owns_paid_org = userInfo.ownsPaidOrg;
    if (userInfo.paidOrgsCount !== undefined) properties.paid_orgs_count = userInfo.paidOrgsCount;

    if (Object.keys(properties).length > 0) {
      const demographics = getUserDemographicsForProfile();
      const identityId = this.userId || this.anonymousId!;
      analytics.identify(identityId, {
        ...properties,
        ...demographics,
        last_updated_at: new Date().toISOString(),
      });

      const logSafeProperties = { ...properties };
      if (logSafeProperties.$email) logSafeProperties.$email = '[REDACTED]';
      if (logSafeProperties.$name) logSafeProperties.$name = '[REDACTED]';
      this.log.debug('User info updated with demographics', logSafeProperties);
    }
  }

  getUserProfile(): UserAnalyticsProfile {
    const storageKey = `galos_profile_${this.anonymousId}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const profile = JSON.parse(stored) as UserAnalyticsProfile;
        profile.lastActiveAt = new Date().toISOString();
        if (this.userId && !profile.userId) {
          profile.userId = this.userId;
        }
        localStorage.setItem(storageKey, JSON.stringify(profile));
        return profile;
      } catch (error) {
        this.log.warn('Failed to parse stored user profile:', error);
      }
    }

    const newProfile: UserAnalyticsProfile = {
      anonymousId: this.anonymousId!,
      userId: this.userId || undefined,
      sessionId: this.sessionId!,
      templates: {},
      preferences: {
        favoriteCategories: [],
        avgSessionLength: 0,
      },
      createdAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
    };

    localStorage.setItem(storageKey, JSON.stringify(newProfile));
    return newProfile;
  }

  updateUserProfile(updates: Partial<UserAnalyticsProfile>): void {
    const profile = this.getUserProfile();
    const updated = { ...profile, ...updates, lastActiveAt: new Date().toISOString() };
    const storageKey = `galos_profile_${this.anonymousId}`;
    localStorage.setItem(storageKey, JSON.stringify(updated));
  }

  trackTemplateUsage(templateId: string, action: 'access' | 'run' | 'complete', metadata?: any): void {
    const profile = this.getUserProfile();

    if (!profile.templates[templateId]) {
      profile.templates[templateId] = {
        accessCount: 0,
        runCount: 0,
        avgRefinements: 0,
        avgCompletionTime: 0,
        lastUsed: new Date().toISOString(),
      };
    }

    const templateData = profile.templates[templateId];
    templateData.lastUsed = new Date().toISOString();

    switch (action) {
      case 'access':
        templateData.accessCount++;
        break;
      case 'run':
        templateData.runCount++;
        break;
      case 'complete':
        if (metadata?.completionTime) {
          const currentAvg = templateData.avgCompletionTime;
          const runs = templateData.runCount;
          templateData.avgCompletionTime = ((currentAvg * (runs - 1)) + metadata.completionTime) / runs;
        }
        if (metadata?.refinements !== undefined) {
          const currentAvg = templateData.avgRefinements;
          const runs = templateData.runCount;
          templateData.avgRefinements = ((currentAvg * (runs - 1)) + metadata.refinements) / runs;
        }
        break;
    }

    this.updateUserProfile(profile);
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

}

export const userTrackingService = new UserTrackingService();
export default userTrackingService;
